/**
 * The side panel: the selected category, or the one being created.
 *
 * Beside the tree rather than in a modal, so the tree stays in view while a
 * node is named, placed and given its families — the decisions this panel
 * asks for are all "where does this sit relative to the rest".
 *
 * **Three kinds of change, three ways of saving.** The fields (name, order,
 * publication, attribute group) are a form: edited together, saved together.
 * Moving is its own button, because it changes the node's level and its whole
 * branch moves with it. Families are attached and detached one at a time and
 * take effect at once, because each one is a decision about that family —
 * which may be leaving another category — not a field of this one.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@hardauto/core-api";
import { useAction } from "@hardauto/core-data";
import { Field } from "@hardauto/core-forms";
import { Badge, Button, Checkbox, Input, Select, useToast } from "@hardauto/core-ui";

import type { AttributeGroup, CatalogApi, CategoryNode, FamilyMapping } from "../../api";
import { NAMESPACE } from "../../i18n";
import { AttributeSetSection } from "./AttributeSetSection";
import { MAX_DEPTH, codeFor, findNode, flatten, fold, moveTargets, trail } from "./tree";

export type PanelMode =
  { kind: "none" } | { kind: "detail"; id: string } | { kind: "create"; parentId: string | null };

export interface CategoryPanelProps {
  api: CatalogApi;
  mode: PanelMode;
  roots: CategoryNode[];
  mappings: FamilyMapping[];
  groups: AttributeGroup[];
  levelLabel: (depth: number) => string;
  mayWrite: boolean;
  /** Something changed on the server: reload the tree and the mapping. */
  onChanged: () => void;
  /** An attribute set, or one of its attributes, changed: reload the sets. */
  onGroupsChanged: () => void;
  onCreated: (id: string) => void;
  onCancel: () => void;
  onDelete: (node: CategoryNode) => void;
  onCreateRoot: () => void;
}

export function CategoryPanel(props: CategoryPanelProps) {
  const { t } = useTranslation(NAMESPACE);
  const { mode, roots } = props;

  if (mode.kind === "create") {
    return (
      <aside className="hardauto-catpanel" aria-label={t("panel.createLabel")}>
        <CreateForm key={mode.parentId ?? "root"} {...props} parentId={mode.parentId} />
      </aside>
    );
  }

  const node = mode.kind === "detail" ? findNode(roots, mode.id) : undefined;
  if (!node) {
    const total = flatten(roots).length;
    const unmapped = props.mappings.filter((row) => !row.is_mapped).length;
    return (
      <aside
        className="hardauto-catpanel hardauto-catpanel--idle"
        aria-label={t("panel.idleLabel")}
      >
        <p className="hardauto-catpanel__hint">{t("panel.idle")}</p>
        <dl className="hardauto-catpanel__facts">
          <div>
            <dt>{t("panel.totalCategories")}</dt>
            <dd className="hardauto-num">{total}</dd>
          </div>
          <div>
            <dt>{t("panel.unmappedFamilies")}</dt>
            <dd className="hardauto-num">{unmapped}</dd>
          </div>
        </dl>
        {props.mayWrite ? (
          <Button variant="primary" onClick={props.onCreateRoot}>
            {t("panel.createRoot")}
          </Button>
        ) : null}
      </aside>
    );
  }

  return (
    <aside
      className="hardauto-catpanel"
      aria-label={t("panel.detailLabel", { label: node.category.label })}
    >
      {/* Keyed on what the server returned, so a save or a reload re-seeds the
          form instead of leaving yesterday's draft over today's values. */}
      <DetailForm
        key={`${node.category.id}:${node.category.label}:${node.category.position}:${node.category.is_active}:${node.category.attribute_group_id ?? ""}:${node.category.parent_id ?? ""}`}
        {...props}
        node={node}
      />
    </aside>
  );
}

// --- Detail -------------------------------------------------------------------

function DetailForm(props: CategoryPanelProps & { node: CategoryNode }) {
  const { t } = useTranslation(NAMESPACE);
  const toast = useToast();
  const action = useAction();
  const { api, node, roots, mappings, groups, levelLabel, mayWrite } = props;
  const { category } = node;

  const [label, setLabel] = useState(category.label);
  const [position, setPosition] = useState(String(category.position));
  const [visible, setVisible] = useState(category.is_active);
  const [parent, setParent] = useState(category.parent_id ?? "");

  const dirty =
    label.trim() !== category.label ||
    Number(position) !== category.position ||
    visible !== category.is_active;
  const moved = parent !== (category.parent_id ?? "");
  const path = trail(roots, category.id);
  const own = mappings.filter((row) => row.category_id === category.id);

  function failed(error: unknown) {
    toast.show(messageOf(error, t), { tone: "danger" });
  }

  return (
    <form
      className="hardauto-catpanel__form"
      onSubmit={(event) => {
        event.preventDefault();
        if (!dirty || !label.trim()) return;
        void action.run("save", async () => {
          try {
            await api.updateCategory(category.id, {
              ...(label.trim() !== category.label ? { label: label.trim() } : {}),
              ...(Number(position) !== category.position ? { position: Number(position) } : {}),
              ...(visible !== category.is_active ? { is_active: visible } : {}),
            });
            toast.show(t("panel.saved"), { tone: "success" });
            props.onChanged();
          } catch (error) {
            failed(error);
          }
        });
      }}
    >
      <header className="hardauto-catpanel__head">
        <span className="hardauto-catpanel__eyebrow">
          {levelLabel(category.depth) || t("panel.level", { depth: category.depth })}
        </span>
        <h2 className="hardauto-catpanel__title">{category.label}</h2>
        {path.length > 1 ? (
          <p className="hardauto-catpanel__trail">{path.slice(0, -1).join(" › ")}</p>
        ) : null}
        {category.is_active ? null : <Badge tone="neutral">{t("tree.hidden")}</Badge>}
      </header>

      <Field label={t("panel.name")} required>
        {(fieldProps) => (
          <Input
            {...fieldProps}
            value={label}
            disabled={!mayWrite}
            onChange={(event) => setLabel(event.target.value)}
          />
        )}
      </Field>

      <div className="hardauto-catpanel__pair">
        <Field label={t("panel.levelLabel")}>
          {(fieldProps) => (
            <Input
              {...fieldProps}
              value={levelLabel(category.depth) || String(category.depth)}
              readOnly
              disabled
            />
          )}
        </Field>
        <Field label={t("panel.position")} hint={t("panel.positionHint")}>
          {(fieldProps) => (
            <Input
              {...fieldProps}
              type="number"
              min={0}
              value={position}
              disabled={!mayWrite}
              onChange={(event) => setPosition(event.target.value)}
            />
          )}
        </Field>
      </div>

      <Checkbox
        label={t("panel.visible")}
        checked={visible}
        disabled={!mayWrite}
        onChange={(event) => setVisible(event.target.checked)}
      />

      {mayWrite ? (
        <div className="hardauto-catpanel__actions">
          <Button
            type="submit"
            variant="primary"
            disabled={!dirty || !label.trim()}
            busy={action.isBusy("save")}
            busyLabel={t("panel.saving")}
          >
            {t("panel.save")}
          </Button>
          <Button
            variant="ghost"
            aria-label={t("categories.deletion.action", { label: category.label })}
            onClick={() => props.onDelete(node)}
          >
            {t("categories.delete")}
          </Button>
        </div>
      ) : null}

      <AttributeSetSection
        api={api}
        node={node}
        roots={roots}
        groups={groups}
        mayWrite={mayWrite}
        onChanged={props.onChanged}
        onGroupsChanged={props.onGroupsChanged}
      />

      <section className="hardauto-catpanel__section" aria-labelledby={`move-${category.id}`}>
        <h3 id={`move-${category.id}`} className="hardauto-catpanel__subtitle">
          {t("panel.parent")}
        </h3>
        <div className="hardauto-catpanel__move">
          <Select
            aria-label={t("panel.parent")}
            value={parent}
            disabled={!mayWrite}
            onChange={(event) => setParent(event.target.value)}
          >
            <option value="">{t("categories.noParent")}</option>
            {moveTargets(roots, node).map(({ category: target, depth }) => (
              <option key={target.id} value={target.id}>
                {`${"  ".repeat(depth)}${target.label}`}
              </option>
            ))}
          </Select>
          {mayWrite ? (
            <Button
              disabled={!moved}
              busy={action.isBusy("move")}
              busyLabel={t("panel.moving")}
              onClick={() => {
                void action.run("move", async () => {
                  try {
                    await api.moveCategory(category.id, parent || null);
                    toast.show(t("panel.moved"), { tone: "success" });
                    props.onChanged();
                  } catch (error) {
                    failed(error);
                  }
                });
              }}
            >
              {t("panel.move")}
            </Button>
          ) : null}
        </div>
      </section>

      <FamiliesSection
        title={t("panel.families")}
        assigned={own}
        all={mappings}
        targetId={category.id}
        mayWrite={mayWrite}
        busy={(code) => action.isBusy(`family-${code}`)}
        onAdd={(code) =>
          void action.run(`family-${code}`, async () => {
            try {
              await api.mapFamily(code, category.id);
              toast.show(t("categories.mapped"), { tone: "success" });
              props.onChanged();
            } catch (error) {
              failed(error);
            }
          })
        }
        onRemove={(code) =>
          void action.run(`family-${code}`, async () => {
            try {
              await api.unmapFamily(code);
              toast.show(t("categories.unmapped_done"), { tone: "success" });
              props.onChanged();
            } catch (error) {
              failed(error);
            }
          })
        }
      />

      <p className="hardauto-catpanel__code">
        {t("panel.code")} <code>{category.code}</code>
      </p>
    </form>
  );
}

// --- Create -------------------------------------------------------------------

function CreateForm(props: CategoryPanelProps & { parentId: string | null }) {
  const { t } = useTranslation(NAMESPACE);
  const toast = useToast();
  const action = useAction();
  const { api, roots, mappings, levelLabel } = props;

  const [parentId, setParentId] = useState(props.parentId ?? "");
  const parent = parentId ? findNode(roots, parentId) : undefined;
  const siblings = parent ? parent.children.length : roots.length;
  const depth = parent ? parent.category.depth + 1 : 1;

  const [label, setLabel] = useState("");
  const [position, setPosition] = useState<string | null>(null);
  const [visible, setVisible] = useState(true);
  const [chosen, setChosen] = useState<string[]>([]);

  // Last among its siblings unless somebody says otherwise; follows the parent
  // while nobody has typed a position.
  const shownPosition = position ?? String(siblings);
  const parents = flatten(roots).filter(({ node }) => node.category.depth < MAX_DEPTH);

  return (
    <form
      className="hardauto-catpanel__form"
      onSubmit={(event) => {
        event.preventDefault();
        if (!label.trim()) return;
        void action.run("create", async () => {
          try {
            const created = await api.createCategory({
              code: codeFor(parent?.category.path ?? "", label.trim()),
              label: label.trim(),
              parent_id: parent ? parent.category.id : null,
              position: Number(shownPosition) || 0,
            });
            if (!visible) await api.updateCategory(created.id, { is_active: false });
            for (const code of chosen) await api.mapFamily(code, created.id);
            toast.show(t("categories.created"), { tone: "success" });
            props.onChanged();
            props.onCreated(created.id);
          } catch (error) {
            toast.show(messageOf(error, t), { tone: "danger" });
          }
        });
      }}
    >
      <header className="hardauto-catpanel__head">
        <span className="hardauto-catpanel__eyebrow">
          {parent ? t("panel.newChild") : t("panel.newRoot")}
        </span>
        <h2 className="hardauto-catpanel__title">{t("panel.createTitle")}</h2>
        {parent ? (
          <p className="hardauto-catpanel__trail">{trail(roots, parent.category.id).join(" › ")}</p>
        ) : null}
      </header>

      <Field label={t("panel.name")} required>
        {(fieldProps) => (
          <Input {...fieldProps} value={label} onChange={(event) => setLabel(event.target.value)} />
        )}
      </Field>

      <div className="hardauto-catpanel__pair">
        <Field label={t("panel.levelLabel")}>
          {(fieldProps) => (
            <Input {...fieldProps} value={levelLabel(depth) || String(depth)} readOnly disabled />
          )}
        </Field>
        <Field label={t("panel.position")} hint={t("panel.positionHint")}>
          {(fieldProps) => (
            <Input
              {...fieldProps}
              type="number"
              min={0}
              value={shownPosition}
              onChange={(event) => setPosition(event.target.value)}
            />
          )}
        </Field>
      </div>

      <Field label={t("panel.parent")}>
        {(fieldProps) => (
          <Select
            {...fieldProps}
            value={parentId}
            onChange={(event) => setParentId(event.target.value)}
          >
            <option value="">{t("categories.noParent")}</option>
            {parents.map(({ node, depth: indent }) => (
              <option key={node.category.id} value={node.category.id}>
                {`${"  ".repeat(indent)}${node.category.label}`}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Checkbox
        label={t("panel.visible")}
        checked={visible}
        onChange={(event) => setVisible(event.target.checked)}
      />

      <FamiliesSection
        title={t("panel.families")}
        assigned={mappings.filter((row) => chosen.includes(row.family_code))}
        all={mappings}
        targetId={null}
        mayWrite
        busy={() => false}
        onAdd={(code) => setChosen((current) => [...current, code])}
        onRemove={(code) => setChosen((current) => current.filter((item) => item !== code))}
      />

      <div className="hardauto-catpanel__actions">
        <Button
          type="submit"
          variant="primary"
          disabled={!label.trim()}
          busy={action.isBusy("create")}
          busyLabel={t("categories.submitting")}
        >
          {t("panel.create")}
        </Button>
        <Button onClick={props.onCancel}>{t("categories.cancel")}</Button>
      </div>
    </form>
  );
}

// --- Families -----------------------------------------------------------------

/**
 * The ERP families attached to the node, and a search to attach more.
 *
 * A family already attached elsewhere is offered with where it is now: one
 * family sits in one category, so attaching it here moves it, and the person
 * doing it should know they are taking it from somewhere.
 */
function FamiliesSection(props: {
  title: string;
  assigned: FamilyMapping[];
  all: FamilyMapping[];
  targetId: string | null;
  mayWrite: boolean;
  busy: (code: string) => boolean;
  onAdd: (code: string) => void;
  onRemove: (code: string) => void;
}) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const [query, setQuery] = useState("");
  const taken = new Set(props.assigned.map((row) => row.family_code));
  const needle = fold(query);
  const matches = needle
    ? props.all
        .filter((row) => !taken.has(row.family_code))
        .filter(
          (row) =>
            fold(row.family_code).includes(needle) || fold(row.family_label).includes(needle),
        )
        // The unmapped first: they are the ones waiting for a home.
        .sort((a, b) => Number(a.is_mapped) - Number(b.is_mapped))
        .slice(0, 8)
    : [];

  return (
    <section className="hardauto-catpanel__section" aria-label={props.title}>
      <h3 className="hardauto-catpanel__subtitle">{props.title}</h3>
      {props.assigned.length === 0 ? (
        <p className="hardauto-cell__muted">{t("panel.noFamily")}</p>
      ) : (
        <ul className="hardauto-catpanel__families">
          {props.assigned.map((row) => (
            <li key={row.family_code}>
              <span className="hardauto-catpanel__family">
                <code>{row.family_code}</code> {row.family_label}
                <span className="hardauto-cell__muted">
                  {" "}
                  · {t("categories.articleCount", { count: row.article_count })}
                </span>
              </span>
              {props.mayWrite ? (
                <Button
                  size="sm"
                  variant="ghost"
                  busy={props.busy(row.family_code)}
                  aria-label={t("panel.removeFamily", { family: row.family_code })}
                  onClick={() => props.onRemove(row.family_code)}
                >
                  {t("panel.remove")}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {props.mayWrite ? (
        <>
          <Input
            type="search"
            aria-label={t("panel.familySearch")}
            placeholder={t("panel.familySearch")}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {needle && matches.length === 0 ? (
            <p className="hardauto-cell__muted">{t("panel.noFamilyMatch")}</p>
          ) : null}
          {matches.length > 0 ? (
            <ul className="hardauto-catpanel__families hardauto-catpanel__families--matches">
              {matches.map((row) => (
                <li key={row.family_code}>
                  <span className="hardauto-catpanel__family">
                    <code>{row.family_code}</code> {row.family_label}
                    <span className="hardauto-cell__muted">
                      {" "}
                      ·{" "}
                      {row.is_mapped && row.category_id !== props.targetId
                        ? t("panel.familyElsewhere", { category: row.category_label })
                        : t("panel.familyFree")}
                      {" · "}
                      {row.article_count.toLocaleString(i18n.language)}
                    </span>
                  </span>
                  <Button
                    size="sm"
                    busy={props.busy(row.family_code)}
                    aria-label={t("panel.addFamily", { family: row.family_code })}
                    onClick={() => {
                      props.onAdd(row.family_code);
                      setQuery("");
                    }}
                  >
                    {t("panel.add")}
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

function messageOf(error: unknown, t: (key: string) => string): string {
  if (error instanceof ApiError) {
    if (error.status === 409) return t("panel.duplicate");
    if (error.message) return error.message;
  }
  return t("error.title");
}
